import { EntryEditError } from '../edits.ts';
import { decodeEntities, readTextBlocks } from '../htmlText.ts';
import { isUpperCase, scanMentions, type Candidate, type Mention, type NameIndex } from '../mentions.ts';
import { text, type SqlConnection } from '../sql.ts';

/**
 * Scan for Connections (docs/PARITY-STEP-5-PLAN.md section 5, decision 5), as the desktop's
 * `mentions.HtmlMentions` and `MentionScanner.unknownNames`: what a scene's manuscript
 * mentions, the capitalized names that match no entry, and the entries already linked;
 * then linking chosen mentions to their entries, and unlinking. It reads the HTML without
 * a DOM (htmlText.ts), as the realm worker and tests can, and only adds or removes the
 * mention links' own tags: every other character of the HTML stays as it was.
 *
 * What a scan was told to ignore is kept in `mention_ignores` under the desktop's keys
 * (`IgnoreKeys`), for the scene (its id is the scope). schema/fixtures/scene-scan-cases.json,
 * written by the desktop, keeps the two in step.
 */

export type EntityTypeGuess = 'PERSON' | 'PLACE' | 'FACTION';

/** A name that matches no entry ("Red Hollow" before it has one), offered as a new entry. */
export interface UnknownName {
  readonly name: string;
  readonly count: number;
  readonly before: string;
  readonly after: string;
  /** A guess from its words ("Hollow", "Keep" and "Road" suggest a place). */
  readonly type: EntityTypeGuess;
}

/** One mention in the text: its number, the words, what's around them, and who it could be. */
export interface Occurrence {
  readonly index: number;
  readonly text: string;
  readonly before: string;
  readonly after: string;
  readonly candidates: readonly Candidate[];
}

export interface SceneScan {
  readonly occurrences: readonly Occurrence[];
  readonly unknownNames: readonly UnknownName[];
  /** Every entry the text already links to, in the order first linked. */
  readonly linkedEntryIds: readonly string[];
}

export const MENTION_HREF_PREFIX = 'realmery:entry/';
const LEGACY_HREF_PREFIX = 'loreforge:entry/';
export const MENTION_LINK_CLASS = 'lf-mention';

/** `IgnoreKeys.forCandidates`: by the entries a mention could mean, so another of their names stays ignored too. */
export function ignoreKeyForCandidates(candidates: readonly Candidate[]): string {
  return `entries:${candidates.map((c) => c.entryId).sort().join(',')}`;
}

/** `IgnoreKeys.forUnknownName`: by the name itself, ignoring case. */
export function ignoreKeyForName(name: string): string {
  return `name:${name.trim().toLowerCase()}`;
}

// ---- scanning ----

/** The text nodes the desktop scans: not inside a link, a script or a style, and not blank. */
function scannedPieces(html: string) {
  return readTextBlocks(html)
    .flatMap((block) => block.pieces)
    .filter((piece) => !piece.inLink && !/^[ \t\n\f\r]*$/.test(piece.text));
}

/** `HtmlMentions.scan`. */
export function scanSceneHtml(html: string, index: NameIndex): SceneScan {
  const occurrences: Occurrence[] = [];
  const all: Mention[] = [];
  let allText = '';
  for (const piece of scannedPieces(html)) {
    const offset = allText.length;
    for (const mention of scanMentions(piece.text, index)) {
      occurrences.push({ index: occurrences.length, text: mention.text, before: before(piece.text, mention.start), after: after(piece.text, mention.end), candidates: mention.candidates });
      all.push({ ...mention, start: offset + mention.start, end: offset + mention.end });
    }
    allText += `${piece.text}\n`;
  }
  return { occurrences, unknownNames: unknownNames(allText, index, all), linkedEntryIds: linkedEntryIds(html) };
}

/**
 * `HtmlMentions.link`: links the chosen occurrences, `chosen` mapping an occurrence's
 * number from `scanSceneHtml` to its entry. The numbers hold while the text is unchanged.
 */
export function linkSceneHtml(html: string, index: NameIndex, chosen: ReadonlyMap<number, string>): string {
  const inserts: { at: number; tag: string }[] = [];
  let number = 0;
  for (const piece of scannedPieces(html)) {
    for (const mention of scanMentions(piece.text, index)) {
      const entryId = chosen.get(number++);
      if (entryId === undefined) continue;
      inserts.push({ at: piece.at[mention.start]!, tag: `<a href="${attribute(MENTION_HREF_PREFIX + entryId)}" class="${MENTION_LINK_CLASS}">` });
      inserts.push({ at: piece.at[mention.end]!, tag: '</a>' });
    }
  }
  return insertAll(html, inserts);
}

/** `HtmlMentions.unlink`: every link to `entryId` back into plain text. */
export function unlinkSceneHtml(html: string, entryId: string): string {
  const cuts: { from: number; to: number }[] = [];
  const tags = readTags(html);
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]!;
    if (tag.name !== 'a' || tag.closing || entryIdOf(tag.href) !== entryId) continue;
    cuts.push({ from: tag.from, to: tag.to });
    const close = tags.slice(i + 1).find((t) => t.name === 'a');
    if (close?.closing) cuts.push({ from: close.from, to: close.to });
  }
  let out = html;
  for (const cut of cuts.sort((a, b) => b.from - a.from)) out = out.slice(0, cut.from) + out.slice(cut.to);
  return out;
}

/** `HtmlMentions.linkedEntryIds`: every entry the text links to, in the order first linked. */
export function linkedEntryIds(html: string): string[] {
  const ids: string[] = [];
  for (const tag of readTags(html)) {
    const id = tag.name === 'a' && !tag.closing ? entryIdOf(tag.href) : null;
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** The entry a `realmery:entry/<id>` (or the older `loreforge:entry/<id>`) link points at. */
export function entryIdOf(href: string | null): string | null {
  if (href === null) return null;
  for (const prefix of [MENTION_HREF_PREFIX, LEGACY_HREF_PREFIX]) if (href.startsWith(prefix) && href.length > prefix.length) return href.slice(prefix.length);
  return null;
}

// ---- names with no entry (MentionScanner.unknownNames) ----

const WORD = /\p{L}[\p{L}\p{Nd}'’-]*/gu;
const CONNECTORS = new Set(['of', 'the', 'de', 'du', 'von', 'van', 'al']);
const STOP_WORDS = new Set(
  ('i a an the he she it we you they his her its our your their this that these those there then when where what who why how but and or nor so yet ' +
    'for if as at by in on of to from with into onto upon not no yes all one some each every both after before while once still even only just now here ' +
    'later soon until though although because since my me him them us was were is are be been had has have did do does said chapter scene act part book ' +
    'morning evening night day monday tuesday wednesday thursday friday saturday sunday sir lady lord captain king queen god gods o oh ah mr mrs ms dr').split(' '),
);
const PLACE_WORDS = new Set(
  ('hollow keep hold haven road pass passes cove hall tower castle gate river forest wood woods mountain mountains lake bridge sea isle island vale valley ' +
    'ford fort fortress city town village reach march marsh barrow crypt temple shrine ruins hills peak peaks coast bay port harbor harbour spire citadel ' +
    'abbey market square street inn tavern mine mines obelisk plains desert wastes').split(' '),
);
const FACTION_WORDS = new Set('house order clan guild brotherhood sisterhood company circle council church cult legion watch empire kingdom'.split(' '));

export function unknownNames(allText: string, index: NameIndex, known: readonly Mention[]): UnknownName[] {
  const claimed = new Array<boolean>(allText.length + 1).fill(false);
  for (const mention of known) for (let i = mention.start; i < mention.end; i++) claimed[i] = true;
  const words: [number, number][] = [...allText.matchAll(WORD)].map((m) => [m.index, m.index + m[0].length]);
  const capital = (w: [number, number]) => isUpperCase(allText[w[0]]!);
  const wordText = (w: [number, number]) => allText.slice(w[0], w[1]);
  const onlySpaceBetween = (a: [number, number], b: [number, number]) => b[0] - a[1] === 1 && allText[a[1]] === ' ';

  // name -> count, first start, first end, whether it's ever seen mid-sentence
  const found = new Map<string, { count: number; start: number; end: number; mid: boolean }>();
  let w = 0;
  while (w < words.length) {
    const word = words[w]!;
    if (!capital(word) || claimed[word[0]]) {
      w++;
      continue;
    }
    let last = w;
    let scan = w + 1;
    while (scan < words.length) {
      const next = words[scan]!;
      if (!onlySpaceBetween(words[scan - 1]!, next) || claimed[next[0]]) break;
      if (capital(next)) {
        last = scan;
        scan++;
        continue;
      }
      // Small joining words ("of", "of the") count only when a capitalized word follows them.
      let ahead = scan;
      while (ahead < words.length && CONNECTORS.has(wordText(words[ahead]!).toLowerCase()) && ahead - scan < 2) ahead++;
      let joined = ahead > scan && ahead < words.length && capital(words[ahead]!);
      for (let k = scan; joined && k < ahead; k++) joined = onlySpaceBetween(words[k]!, words[k + 1]!);
      if (!joined) break;
      scan = ahead;
    }
    let first = w;
    // A leading "The" belongs to the sentence, not the name.
    if (last > first && wordText(words[first]!).toLowerCase() === 'the') first++;
    const start = words[first]![0];
    const end = trimPossessive(allText, words[last]![1]);
    const name = allText.slice(start, end);
    const single = first === last;
    const sentenceStart = isSentenceStart(allText, words[w]![0]);
    if (name.length >= 3 && !index.knows(name) && !(single && STOP_WORDS.has(name.toLowerCase()))) {
      let entry = found.get(name);
      if (!entry) {
        entry = { count: 0, start, end, mid: false };
        found.set(name, entry);
      }
      entry.count++;
      if (!single || !sentenceStart || first > w) entry.mid = true;
    }
    w = last + 1;
  }
  return [...found.entries()]
    .filter(([, data]) => data.mid) // never only the first word of a sentence
    .map(([name, data]) => ({ name, count: data.count, before: before(allText, data.start), after: after(allText, data.end), type: guessType(name) }))
    .sort((a, b) => b.count - a.count);
}

/** "Red Hollow" a place; "House Ashcombe" a faction; anything else a person. */
export function guessType(name: string): EntityTypeGuess {
  const words = name.toLowerCase().split(/[ \t\n\x0B\f\r]+/);
  if (words.length > 1 && (FACTION_WORDS.has(words[0]!) || FACTION_WORDS.has(words.at(-1)!))) return 'FACTION';
  return words.some((word) => PLACE_WORDS.has(word)) ? 'PLACE' : 'PERSON';
}

/** Up to 60 characters before `index`, cut at a word (`MentionScanner.before`). */
export function before(text: string, index: number): string {
  const from = Math.max(0, index - 60);
  let part = text.slice(from, index);
  if (from > 0) {
    const space = part.indexOf(' ');
    part = `…${space >= 0 ? part.slice(space + 1) : part}`;
  }
  return part.replace(/[ \t\n\x0B\f\r]+/g, ' ');
}

/** Up to 60 characters after `index`, cut at a word (`MentionScanner.after`). */
export function after(text: string, index: number): string {
  const to = Math.min(text.length, index + 60);
  let part = text.slice(index, to);
  if (to < text.length) {
    const space = part.lastIndexOf(' ');
    part = `${space > 0 ? part.slice(0, space) : part}…`;
  }
  return part.replace(/[ \t\n\x0B\f\r]+/g, ' ');
}

function trimPossessive(text: string, end: number): number {
  if (end >= 2 && (text.startsWith("'s", end - 2) || text.startsWith('’s', end - 2))) return end - 2;
  if (end >= 1 && (text[end - 1] === "'" || text[end - 1] === '’')) return end - 1;
  return end;
}

function isSentenceStart(text: string, index: number): boolean {
  let i = index - 1;
  while (i >= 0 && ' "“\'‘('.includes(text[i]!)) i--;
  return i < 0 || '.!?:\n\r—'.includes(text[i]!);
}

// ---- tags ----

interface Tag {
  readonly name: string;
  readonly closing: boolean;
  readonly from: number;
  readonly to: number;
  /** The decoded `href`, or null. */
  readonly href: string | null;
}

/** The HTML's tags in order, skipping comments, declarations, and what's inside a script or style. */
function readTags(html: string): Tag[] {
  const tags: Tag[] = [];
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    if (lt < 0) break;
    if (html.startsWith('<!--', lt)) {
      const end = html.indexOf('-->', lt + 4);
      i = end < 0 ? html.length : end + 3;
      continue;
    }
    if (html[lt + 1] === '!' || html[lt + 1] === '?') {
      const end = html.indexOf('>', lt);
      i = end < 0 ? html.length : end + 1;
      continue;
    }
    const match = /^<(\/?)([A-Za-z][A-Za-z0-9-]*)/.exec(html.slice(lt, lt + 64));
    if (!match) {
      i = lt + 1;
      continue;
    }
    const end = endOfTag(html, lt + match[0].length);
    const name = match[2]!.toLowerCase();
    const closing = match[1] === '/';
    tags.push({ name, closing, from: lt, to: end, href: closing ? null : hrefOf(html.slice(lt + match[0].length, end)) });
    i = end;
    if (!closing && (name === 'script' || name === 'style')) {
      const close = html.toLowerCase().indexOf(`</${name}`, i);
      i = close < 0 ? html.length : close;
    }
  }
  return tags;
}

function endOfTag(html: string, from: number): number {
  let quote: string | null = null;
  for (let i = from; i < html.length; i++) {
    const c = html[i]!;
    if (quote) {
      if (c === quote) quote = null;
    } else if (c === '"' || c === "'") quote = c;
    else if (c === '>') return i + 1;
  }
  return html.length;
}

function hrefOf(attributes: string): string | null {
  const match = /(?:^|[\s/])href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(attributes);
  return match ? decodeEntities(match[1] ?? match[2] ?? match[3] ?? '') : null;
}

function attribute(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');
}

function insertAll(html: string, inserts: { at: number; tag: string }[]): string {
  // Last first, so earlier places stay where they are; at one place, closing before opening.
  let out = html;
  const order = inserts.map((insert, i) => ({ ...insert, i })).sort((a, b) => b.at - a.at || b.i - a.i);
  for (const { at, tag } of order) out = out.slice(0, at) + tag + out.slice(at);
  return out;
}

// ---- what was ignored (MentionIgnoreRepository) ----

export interface IgnoredMention {
  readonly key: string;
  readonly label: string;
}

const IGNORE_KEY_MAX = 4000;
const IGNORE_LABEL_MAX = 1000;

/** What scans in `scope` were told to leave alone, by label. */
export function listIgnoredMentions(conn: SqlConnection, scope: string): IgnoredMention[] {
  return conn.all('SELECT key, label FROM mention_ignores WHERE scope = ? ORDER BY label COLLATE NOCASE', [scope]).map((row) => ({ key: text(row['key']), label: text(row['label']) }));
}

export function ignoreMention(conn: SqlConnection, scope: string, key: string, label: string, now: string = new Date().toISOString()): void {
  if (!/^(entries|name):/.test(key) || key.length > IGNORE_KEY_MAX || label.length > IGNORE_LABEL_MAX || scope.length > 200) {
    throw new EntryEditError('invalid', 'That mention could not be ignored.');
  }
  conn.run('INSERT INTO mention_ignores (scope, key, label, added) VALUES (?, ?, ?, ?) ON CONFLICT(scope, key) DO UPDATE SET label = excluded.label', [scope, key, label, now]);
}

export function stopIgnoringMention(conn: SqlConnection, scope: string, key: string): void {
  conn.run('DELETE FROM mention_ignores WHERE scope = ? AND key = ?', [scope, key]);
}

/** A scan less what was ignored here (`ConnectionScanDialog.withoutIgnored`). */
export function withoutIgnored(scan: SceneScan, ignored: Iterable<string>): SceneScan {
  const keys = new Set(ignored);
  if (keys.size === 0) return scan;
  return {
    occurrences: scan.occurrences.filter((o) => !keys.has(ignoreKeyForCandidates(o.candidates))),
    unknownNames: scan.unknownNames.filter((n) => !keys.has(ignoreKeyForName(n.name))),
    linkedEntryIds: scan.linkedEntryIds,
  };
}
